import { RiskDataService } from "@app/risk/services/RiskDataService";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { IHasGuid } from "@app/shared/interfaces/IHasGuid";
import { ItemLinkableBase } from "@app/shared/linkable/item-linkable-base";
import { Observable, firstValueFrom } from "rxjs";
import { Component, Input, inject } from "@angular/core";
import { RendererType } from "@enums/RendererType";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { ModuleType } from "@enums/ModuleType";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { nameof } from "@methods/jeffs-toolkit";
import { FilterType } from "@enums/FilterType";
import { RiskLinkModel } from "@models/risk/RiskLinkModel";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { TranslocoService, provideTranslocoScope } from "@jsverse/transloco";

@Component({
    selector: "risk-linkable",
    templateUrl: "./risk-linkable.component.html",
    styleUrls: ["./risk-linkable.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("risk")],
})
export class RiskLinkableComponent extends ItemLinkableBase {
    @Input() moduleType = ModuleType.ORM;

    @Input() typename = "risks";
    @Input() activeFilters: SearchKeyword[] = [];
    @Input() showAdvancedFilters = false;

    hasAccessChecks = {};
    accessCheckInProgress = false;

    filterConfig: FilterConfig[] = [
        {
            label: "Organizations",
            column: nameof<RiskLinkModel>((x) => x.organizationId),
            type: FilterType.Organization,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Business Dimensions",
            column: nameof<RiskLinkModel>((x) => x.businessDimensionIds),
            type: FilterType.BusinessDimensions,
            hideActionColumn: true,
            param: "",
        },
        {
            label: "Framework Dimensions",
            column: nameof<RiskLinkModel>((x) => x.frameworkDimensionIds),
            type: FilterType.FrameworkDimensions,
            hideActionColumn: true,
            param: "",
        },
    ];

    numberProperties = ["Gross score", "Net score"];
    rendererConfig: RendererConfig[] = [
        {
            textColumn: "Gross score",
            actionColumn: "GrossScoreColor",
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: "Net score",
            actionColumn: "NetScoreColor",
            hideActionColumn: true,
            type: RendererType.Score,
        },
    ];

    private _riskDS = inject(RiskDataService);
    private _pages = inject(Pages);
    private _tabService = inject(TabService);
    private _translocoService = inject(TranslocoService);

    hasAccessToItem(guid: string): Observable<boolean> {
        return this._riskDS.hasAccessToRisk(guid);
    }

    async openTab(item: IHasGuid, hasAccess: boolean) {
        if (hasAccess === true) {
            this._tabService.generateTab(
                this._pages.RiskDetail,
                item.guid,
                `(R) ${item[this.displayValueProp]}`,
            );
        } else {
            // Nothing in this template uses the transloco pipe/directive, so the lazy scope
            // is not loaded yet and a synchronous translate() would return the raw key.
            await firstValueFrom(
                this._translocoService.load(`risk/${this._translocoService.getActiveLang()}`),
            );
            this._toastr.warning(
                this._translocoService.translate("risk.riskLinkable.notEnoughRights"),
            );
        }
    }
}
